/**
 * 창(모달) 쌓임 — **맨 위 창 하나만** ESC 를 받는다 (v2.231.0 · 점검 H-4 · 사용자 결정 2026-10-06).
 *
 * 사용자 결정: 「입력하는 창은 ESC 로 안 닫힘 · 보기만 하는 창만 닫힘」.
 *
 * 왜 한 곳으로 모았나 (점검 실측)
 *   · 창마다 window 에 ESC 귀를 따로 달아서, 위에 뜬 창에서 ESC 를 한 번 누르면 **아래 창까지 같이** 닫혔다
 *     (영업건 상세 위의 계산서 발행요청·거래명세서 창 → 상세까지 닫혀 적던 글이 사라짐 · 품목 이력 위의 입출고 상세 → 둘 다 닫힘).
 *   · 입력 창의 「닫기 확인」 장치(useDirtyClose)도 ESC 를 받아, 아무것도 안 적었으면 바로 닫고
 *     「저장할까요?」 창에서 [취소]를 누르면 저장 없이 닫았다.
 *
 * 규칙
 *   · 열린 창은 useDialogLayer(열림, ESC 때 할 일) 로 자기를 쌓는다 — 늦게 연 창이 위.
 *   · ESC 는 **맨 위 창**에만 간다. 그 창이 입력 창이면(할 일 = null) 아무 일도 없다 — 아래 창으로 새지 않는다.
 *   · 입력칸·드롭다운이 ESC 를 먼저 썼으면(목록 닫기·검색어 지우기 — preventDefault) 창은 닫지 않는다.
 *   · 한글 입력 중(조합 중) ESC 는 조합 취소일 뿐이라 무시한다.
 */

import { useEffect, useRef } from 'react';

interface Layer {
  id: number;
  /** ESC 때 할 일 — 지금 값을 읽는다(닫힌 값이 남지 않게) · null 이면 입력 창(아무 일도 없음) */
  esc: () => (() => void) | null;
}

const stack: Layer[] = [];
let seq = 0;
let installed = false;

function onKeyDown(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  // 맨 위 창이 받았다 — 입력 창이어도 「받았다」로 친다(아래 창으로 새지 않게)
  e.preventDefault();
  const fn = top.esc();
  if (fn) fn();
}

function install(): void {
  if (installed || typeof window === 'undefined') return;
  window.addEventListener('keydown', onKeyDown);
  installed = true;
}

/** 지금 쌓인 창 수 — 점검·그물용 */
export function dialogDepth(): number {
  return stack.length;
}

/**
 * 이 창을 쌓는다.
 * @param active 열려 있나 (닫혀 있는데 그려지는 부품이면 false)
 * @param onEsc  ESC 때 할 일 — 보기 창은 닫기, **입력 창은 null**(ESC 로 안 닫힘)
 */
export function useDialogLayer(active: boolean, onEsc: (() => void) | null): void {
  const ref = useRef(onEsc);
  ref.current = onEsc;
  useEffect(() => {
    if (!active) return;
    install();
    const layer: Layer = { id: ++seq, esc: () => ref.current };
    stack.push(layer);
    return () => {
      const i = stack.indexOf(layer);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [active]);
}
